import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { ChevronDownIcon, ChevronRightIcon, CalendarIcon } from '@heroicons/react/24/outline';
import AddStepButton from '../steps/AddStepButton';

const ProjectGrid = ({ 
  phases, 
  phaseStatusTypes = [], 
  stepStatusTypes = [], 
  handleStepClick, 
  handleStepAdded,
  handlePhaseClick,
  readOnly = false
}) => {
  const navigate = useNavigate();
  const [expandedPhases, setExpandedPhases] = useState({});
  
  // Initialize all phases as expanded by default
  useEffect(() => {
    if (phases && phases.length > 0) {
      const initialExpandedState = {};
      phases.forEach(phase => {
        if (phase.id) {
          initialExpandedState[phase.id] = true;
        }
      });
      setExpandedPhases(initialExpandedState);
    }
  }, [phases]);
  
  const togglePhaseExpand = (phaseId, e) => {
    e.stopPropagation();
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };
  
  const getStatusColor = (statusName, statusTypes) => {
    return statusTypes.find(s => s.name === statusName)?.color || '#6B7280';
  };
  
  // Format due date for display
  const formatDueDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  // Check if due date is approaching (within 7 days) or overdue
  const getDueDateStatus = (dateString) => {
    if (!dateString) return null;
    
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate days difference
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'overdue', className: 'bg-red-100 text-red-800' };
    } else if (diffDays <= 7) {
      return { status: 'approaching', className: 'bg-yellow-100 text-yellow-800' };
    }
    
    return { status: 'normal', className: 'bg-gray-100 text-gray-600' };
  };
  
  if (!phases || phases.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        No phases found for this project.
      </div>
    );
  }
  
  return (
    <div className="overflow-hidden bg-white shadow rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Phase/Step
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Due Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {phases.map((phase, phaseIndex) => {
            const isExpanded = phase.id ? expandedPhases[phase.id] : false;
            const phaseStatusColor = getStatusColor(phase.status, phaseStatusTypes);
            const steps = Array.isArray(phase.steps) ? phase.steps : [];
            const phaseDueDate = formatDueDate(phase.due_date);
            const phaseDueDateStatus = getDueDateStatus(phase.due_date);
            
            // Determine if this is an even or odd phase for alternating colors
            const isEvenPhase = phaseIndex % 2 === 0;
            
            return (
              <React.Fragment key={phase.id || `phase-${phaseIndex}`}>
                {/* Phase Row */}
                <tr 
                  className={`${isEvenPhase ? 'bg-blue-50' : 'bg-slate-100'} hover:bg-blue-100 cursor-pointer`} 
                  onClick={() => phase.id && handlePhaseClick && handlePhaseClick(phase.id)}
                >
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      {phase.id && steps.length > 0 && (
                        <button
                          onClick={(e) => togglePhaseExpand(phase.id, e)}
                          className="mr-2 text-blue-500 hover:text-blue-700 focus:outline-none"
                        >
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      <div className="text-sm font-bold text-gray-900">
                        {phase.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {phase.status && (
                      <span
                        className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                        style={{
                          backgroundColor: phaseStatusColor,
                          color: 'white',
                        }}
                      >
                        {phase.status}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {phaseDueDate ? (
                      <div className={`px-2 py-1 inline-flex items-center text-xs rounded ${phaseDueDateStatus?.className}`}>
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        {phaseDueDate}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">Not set</span>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-xs text-gray-600 truncate font-medium" style={{ maxWidth: '300px' }}>
                      {phase.description || 'No description'}
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                    {phase.id && !readOnly && (
                      <AddStepButton
                        phaseId={phase.id}
                        onStepAdded={() => handleStepAdded && handleStepAdded(phase.id)}
                        label="Add Step"
                      />
                    )}
                  </td>
                </tr>
                
                {/* Step Rows */}
                {phase.id && isExpanded && steps.map((step, stepIndex) => {
                  const stepStatusColor = getStatusColor(step.status, stepStatusTypes);
                  const stepDueDate = formatDueDate(step.due_date);
                  const stepDueDateStatus = getDueDateStatus(step.due_date);
                  
                  return (
                    <tr
                      key={step.id || `${phase.id}-step-${stepIndex}`}
                      className="bg-white cursor-pointer hover:bg-gray-50 border-t border-gray-100"
                      onClick={() => step.id && handleStepClick && handleStepClick(step)}
                    >
                      <td className="px-6 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="ml-7 text-sm text-gray-900">
                            {step.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap">
                        {step.status && (
                          <span
                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                            style={{
                              backgroundColor: stepStatusColor,
                              color: 'white',
                            }}
                          >
                            {step.status || 'Not Started'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap">
                        {stepDueDate ? (
                          <div className={`px-2 py-1 inline-flex items-center text-xs rounded ${stepDueDateStatus?.className}`}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {stepDueDate}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Not set</span>
                        )}
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-500 truncate" style={{ maxWidth: '300px' }}>
                          {step.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                        {step.id && (
                          readOnly ? (
                            <button
                              className="text-blue-600 hover:text-blue-900 text-xs bg-transparent border-none cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStepClick && handleStepClick(step);
                              }}
                            >
                              View details
                            </button>
                          ) : (
                            <button
                              className="text-blue-600 hover:text-blue-900 text-xs bg-transparent border-none cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStepClick && handleStepClick(step);
                              }}
                            >
                              View details
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ProjectGrid; 